'use client';

import React, { useState } from 'react';
import { Edit2, Trash2, Eye, ChevronUp, ChevronDown, Search, Hash, Calendar } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';
import { OwnerListing } from '@/lib/ownerListings';

interface ListingsTableProps {
  listings: OwnerListing[];
  onEdit: (l: OwnerListing) => void;
  onDelete: (id: string) => void;
  onStatusChange: (id: string, status: OwnerListing['status']) => void;
}

type SortField = 'title' | 'monthlyRent' | 'views' | 'inquiries' | 'listedOn';

const STATUS_OPTIONS: OwnerListing['status'][] = ['Active', 'Pending Review', 'Draft', 'Rented Out'];

function StatusBadge({ status }: { status: OwnerListing['status'] }) {
  const map: Record<OwnerListing['status'], string> = {
    Active: 'badge-active',
    'Pending Review': 'badge-pending',
    Rejected: 'badge-rejected',
    Draft: 'badge-draft',
    'Rented Out': 'badge-rented',
  };
  return <span className={map[status]}>{status}</span>;
}

export default function ListingsTable({ listings, onEdit, onDelete, onStatusChange }: ListingsTableProps) {
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('listedOn');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [openStatusId, setOpenStatusId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8;

  const filtered = listings.filter((l) =>
    l.title.toLowerCase().includes(search.toLowerCase()) ||
    l.city.toLowerCase().includes(search.toLowerCase()) ||
    (l.listingNumber?.toLowerCase().includes(search.toLowerCase()) ?? false)
  );

  const sorted = [...filtered].sort((a, b) => {
    let aVal: string | number = a[sortField] as string | number;
    let bVal: string | number = b[sortField] as string | number;
    if (sortField === 'listedOn') {
      aVal = new Date(a.listedOn).getTime();
      bVal = new Date(b.listedOn).getTime();
    }
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sorted.length / pageSize);
  const paginated = sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
    setCurrentPage(1);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronUp size={12} className="opacity-30" />;
    return sortDir === 'asc' ? (
      <ChevronUp size={12} className="text-primary" />
    ) : (
      <ChevronDown size={12} className="text-primary" />
    );
  };

  return (
    <div className="section-card p-0 overflow-hidden">
      {/* Table Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-5 border-b border-border">
        <div>
          <h2 className="text-base font-bold text-foreground">My Listings</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {filtered.length} {filtered.length === 1 ? 'listing' : 'listings'} total
          </p>
        </div>
        <div className="relative w-full sm:w-64">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            placeholder="Search listings..."
            className="input-field pl-9 h-9 text-sm"
            aria-label="Search listings"
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="bg-muted/50 border-b border-border">
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-72">
                Property
              </th>
              <th
                className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground select-none"
                onClick={() => toggleSort('monthlyRent')}
              >
                <div className="flex items-center gap-1">
                  Rent <SortIcon field="monthlyRent" />
                </div>
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                BHK / Type
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th
                className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground select-none"
                onClick={() => toggleSort('views')}
              >
                <div className="flex items-center gap-1">
                  Views <SortIcon field="views" />
                </div>
              </th>
              <th
                className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground select-none"
                onClick={() => toggleSort('inquiries')}
              >
                <div className="flex items-center gap-1">
                  Inquiries <SortIcon field="inquiries" />
                </div>
              </th>
              <th
                className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground select-none"
                onClick={() => toggleSort('listedOn')}
              >
                <div className="flex items-center gap-1">
                  Listed On <SortIcon field="listedOn" />
                </div>
              </th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-16 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                      <Eye size={20} className="text-muted-foreground" />
                    </div>
                    <div className="text-sm font-semibold text-foreground">No listings found</div>
                    <div className="text-xs text-muted-foreground">
                      {search ? 'Try a different search term' : 'Add your first property listing'}
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              paginated.map((listing) => (
                <tr
                  key={listing.id}
                  className="hover:bg-muted/40 transition-colors group"
                >
                  {/* Property */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-10 rounded-lg overflow-hidden bg-muted shrink-0">
                        <AppImage
                          src={listing.coverPhoto}
                          alt={`${listing.title} cover photo`}
                          width={56}
                          height={40}
                          className="object-cover w-full h-full"
                        />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-foreground truncate max-w-[180px]">
                          {listing.title}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {listing.locality}, {listing.city}
                        </div>
                        {listing.listingNumber && (
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-mono font-semibold bg-primary/10 text-primary border border-primary/20">
                              {listing.listingNumber}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Rent */}
                  <td className="px-4 py-3">
                    <div className="text-sm font-bold text-foreground tabular-nums">
                      ₹{listing.monthlyRent.toLocaleString('en-IN')}
                    </div>
                    <div className="text-xs text-muted-foreground">/month</div>
                  </td>

                  {/* BHK / Type */}
                  <td className="px-4 py-3">
                    <div className="text-sm font-semibold text-foreground">{listing.bhk}</div>
                    <div className="text-xs text-muted-foreground">{listing.houseType}</div>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3 relative">
                    <button
                      onClick={() => setOpenStatusId(openStatusId === listing.id ? null : listing.id)}
                      className="hover:opacity-80 transition-opacity"
                      aria-label={`Change status for ${listing.title}`}
                      aria-expanded={openStatusId === listing.id}
                    >
                      <StatusBadge status={listing.status} />
                    </button>
                    {/* Listing number */}
                    {(listing as any).listingNumber && (
                      <div className="flex items-center gap-1 mt-1 text-xs text-primary font-semibold">
                        <Hash size={9} />{(listing as any).listingNumber}
                      </div>
                    )}
                    {/* Validity date */}
                    {(listing as any).expiresAt && listing.status === 'Active' && (
                      <div className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground">
                        <Calendar size={9} />
                        {(() => {
                          const d = new Date((listing as any).expiresAt);
                          const now = new Date();
                          const daysLeft = Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                          if (daysLeft <= 0) return <span className="text-red-500">Expired</span>;
                          if (daysLeft <= 3) return <span className="text-amber-600">{daysLeft}d left</span>;
                          return <span>Valid till {d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</span>;
                        })()}
                      </div>
                    )}

                    {openStatusId === listing.id && (
                      <div className="absolute z-20 top-full left-4 mt-1 bg-card border border-border rounded-xl shadow-modal py-1 min-w-[160px] fade-in">
                        {STATUS_OPTIONS.map((s) => (
                          <button
                            key={`status-opt-${listing.id}-${s}`}
                            onClick={() => {
                              onStatusChange(listing.id, s);
                              setOpenStatusId(null);
                            }}
                            className={`w-full text-left px-3 py-2 text-xs font-semibold hover:bg-muted transition-colors ${
                              listing.status === s ? 'text-primary' : 'text-foreground'
                            }`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                  </td>

                  {/* Views */}
                  <td className="px-4 py-3">
                    <div className="text-sm font-semibold text-foreground tabular-nums">
                      {listing.views.toLocaleString('en-IN')}
                    </div>
                  </td>

                  {/* Inquiries */}
                  <td className="px-4 py-3">
                    <div className="text-sm font-semibold text-foreground tabular-nums">
                      {listing.inquiries}
                    </div>
                  </td>

                  {/* Listed On */}
                  <td className="px-4 py-3">
                    <div className="text-xs text-muted-foreground">{listing.listedOn}</div>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => onEdit(listing)}
                        className="p-1.5 rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                        title={`Edit ${listing.title}`}
                        aria-label={`Edit ${listing.title}`}
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => onDelete(listing.id)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-red-600 transition-colors"
                        title={`Delete ${listing.title} — this cannot be undone`}
                        aria-label={`Delete ${listing.title}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-5 py-4 border-t border-border">
          <div className="text-xs text-muted-foreground">
            Showing {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, sorted.length)} of {sorted.length} listings
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={`page-${i + 1}`}
                onClick={() => setCurrentPage(i + 1)}
                className={`w-8 h-8 rounded-lg text-xs font-bold transition-colors ${
                  currentPage === i + 1
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted text-muted-foreground'
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}